/**
 * KanbanCardDesigner — wizualny kreator wyglądu kafelka Kanban („Edytuj wygląd karty").
 *
 * Odwzorowanie designera kafelka z SellCalla: paleta (pola listy + elementy
 * layoutu) → kanwa pojedynczej kartki (drag&drop + zagnieżdżone kontenery) →
 * panel właściwości zaznaczonego elementu → podgląd. Wynik zapisywany jako
 * `cardLayout: LayoutElement[]` (kształt z `lib/layout-tree.ts`, ten sam, który
 * waliduje backend) w `kanbanConfig`.
 *
 * Cienka skorupa na GENERYCZNYCH prymitywach kreatora layoutu — NIE kopia
 * `SystemModuleBuilderPage`:
 *  - drzewo + mutacje: `lib/layout-tree.ts` (insert/remove/update/reorder/move),
 *  - drag&drop: `hooks/usePaletteDnD.ts` (paleta → kontener + reorder + cross-move),
 *  - render elementów: `LayoutElementRegistry` (kontenery/treść/komunikaty),
 *  - podgląd kafelka: wspólny `renderKanbanCardLayout`.
 *
 * Stan wewnętrzny trzyma drzewo w „opakowanym korzeniu" (jeden syntetyczny
 * kontener ROOT), dzięki czemu wszystkie helpery z `layout-tree` (które działają
 * tylko na kontenerach OBECNYCH w drzewie) obsługują też poziom najwyższy bez
 * specjalnych ścieżek. Przy zapisie korzeń jest rozpakowywany do `root.children`.
 */

import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Boxes, Eye, GripVertical, Pencil, Type as TypeIcon, X } from 'lucide-react';

import {
  type LayoutElement as RegistryLayoutElement,
  type LayoutElementDefinition,
  LayoutElementRegistry,
} from '@/components/fields/LayoutElementRegistry';
import { type PaletteDragData, usePaletteDnD } from '@/hooks/usePaletteDnD';
import {
  findInTree,
  insertIntoContainer,
  type LayoutElement,
  moveInTree,
  removeFromTree,
  reorderInContainer,
  updateInTree,
} from '@/lib/layout-tree';

import { renderKanbanCardLayout } from './kanban-card-layout';

const ROOT_ID = '__kanban_card_root__';
const ROOT_TYPE = '__kanban_card_root__';

/** Kategorie elementów sensowne na kafelku (bez struktury strony i interaktywnych). */
const CARD_CATEGORIES = ['kontenery', 'tresc', 'komunikaty'] as const;

interface CardField {
  id: string;
  label: string;
}

interface KanbanCardDesignerProps {
  open: boolean;
  onClose: () => void;
  /** Pola dostępne na kartce (kolumny listy: id + etykieta). */
  fields: CardField[];
  /** Bieżący układ (`null` = brak — start od pustej kartki). */
  initialLayout: LayoutElement[] | null;
  /** Zapis układu — wywoływane z rozpakowanym `root.children`. */
  onSave: (layout: LayoutElement[]) => void;
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `el_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

function isContainerEl(el: LayoutElement): boolean {
  if (Array.isArray(el.children)) return true;
  return Boolean(LayoutElementRegistry.get(el.type)?.isContainer);
}

function collectFieldIds(elements: LayoutElement[]): Set<string> {
  const used = new Set<string>();
  const walk = (arr: LayoutElement[]) => {
    for (const el of arr) {
      if (el.type === 'field' && el.fieldId) used.add(el.fieldId);
      if (el.children) walk(el.children);
    }
  };
  walk(elements);
  return used;
}

export function KanbanCardDesigner({
  open,
  onClose,
  fields,
  initialLayout,
  onSave,
}: KanbanCardDesignerProps): React.ReactElement | null {
  const { t } = useTranslation();

  // Drzewo z opakowanym korzeniem — patrz nagłówek pliku.
  const [tree, setTree] = useState<LayoutElement[]>(() => [
    { id: ROOT_ID, type: ROOT_TYPE, children: initialLayout ? structuredClone(initialLayout) : [] },
  ]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setView] = useState<'edit' | 'preview'>('edit');

  const rootChildren = useMemo<LayoutElement[]>(() => tree[0]?.children ?? [], [tree]);
  const usedFieldIds = useMemo(() => collectFieldIds(rootChildren), [rootChildren]);

  const layoutDefs = useMemo<LayoutElementDefinition[]>(
    () => CARD_CATEGORIES.flatMap((cat) => LayoutElementRegistry.getByCategory(cat)),
    [],
  );

  const selected = useMemo<LayoutElement | null>(() => {
    if (!selectedId) return null;
    const found = findInTree(tree, selectedId);
    return found && 'type' in found ? (found as LayoutElement) : null;
  }, [tree, selectedId]);

  // Kontener docelowy dla „kliknij by dodać": zaznaczony kontener albo korzeń.
  const addTargetId = selected && isContainerEl(selected) ? (selectedId as string) : ROOT_ID;

  // ── Mutacje ────────────────────────────────────────────────────────────────
  const addField = (fieldId: string) => {
    if (usedFieldIds.has(fieldId)) return;
    const el: LayoutElement = { id: newId(), type: 'field', fieldId };
    setTree((prev) => insertIntoContainer(prev, addTargetId, el));
    setSelectedId(el.id);
  };

  const addLayoutElement = (type: string) => {
    const def = LayoutElementRegistry.get(type);
    const el: LayoutElement = {
      id: newId(),
      type,
      settings: def?.defaultSettings() ?? {},
      ...(def?.isContainer ? { children: [] } : {}),
    };
    setTree((prev) => insertIntoContainer(prev, addTargetId, el));
    setSelectedId(el.id);
  };

  const removeElement = (id: string) => {
    setTree((prev) => removeFromTree(prev, id));
    setSelectedId((cur) => (cur === id ? null : cur));
  };

  const updateSettings = (id: string, patch: Record<string, unknown>) => {
    const node = findInTree(tree, id);
    const current = node && 'settings' in node ? ((node as LayoutElement).settings ?? {}) : {};
    setTree((prev) => updateInTree(prev, id, { settings: { ...current, ...patch } }));
  };

  // ── Drag&drop (paleta → kontener, reorder, cross-move) ───────────────────────
  const insertIndexInContainer = (containerId: string, overId: string): number | undefined => {
    const node = findInTree(tree, containerId);
    const arr = (node as { children?: LayoutElement[] } | null)?.children;
    if (!arr) return undefined;
    const idx = arr.findIndex((c) => c.id === overId);
    return idx >= 0 ? idx : undefined;
  };

  const { sensors, onDragEnd } = usePaletteDnD({
    onAddFromPalette: (kind, payload, sectionId) => {
      if (kind === 'field') {
        if (usedFieldIds.has(payload)) return;
        const el: LayoutElement = { id: newId(), type: 'field', fieldId: payload };
        setTree((prev) => insertIntoContainer(prev, sectionId, el));
        setSelectedId(el.id);
        return;
      }
      const def = LayoutElementRegistry.get(payload);
      const el: LayoutElement = {
        id: newId(),
        type: payload,
        settings: def?.defaultSettings() ?? {},
        ...(def?.isContainer ? { children: [] } : {}),
      };
      setTree((prev) => insertIntoContainer(prev, sectionId, el));
      setSelectedId(el.id);
    },
    onSectionReorder: () => {
      /* kafelek nie ma osobnych „sekcji" — wszystko to elementy */
    },
    onElementReorder: (sourceContainerId, activeId, targetContainerId, overId) => {
      if (sourceContainerId === targetContainerId) {
        setTree((prev) => reorderInContainer(prev, sourceContainerId, activeId, overId));
      } else {
        const index = insertIndexInContainer(targetContainerId, overId);
        setTree((prev) => moveInTree(prev, activeId, targetContainerId, index));
      }
    },
    onAccordionPanelReorder: () => {},
    onStepReorder: () => {},
  });

  if (!open) return null;

  const handleSave = () => {
    onSave(structuredClone(rootChildren));
    onClose();
  };

  const fieldLabel = (fieldId: string): string =>
    fields.find((f) => f.id === fieldId)?.label ?? fieldId;

  // Podgląd: pole renderowane jako etykieta + placeholder wartości.
  const renderFieldPreview = (fieldId: string): ReactNode => (
    <div className="flex items-baseline gap-1 text-xs">
      <span className="shrink-0 text-text-muted">{fieldLabel(fieldId)}:</span>
      <span className="truncate text-text-tertiary">—</span>
    </div>
  );

  return (
    <div
      className="flex h-[70vh] flex-col overflow-hidden rounded-lg border border-border-secondary bg-bg-primary"
      data-testid="kanban-card-designer"
    >
      {/* Pasek górny */}
      <div className="flex items-center justify-between border-b border-border-secondary px-4 py-2.5">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-text-primary">
            {t('tenant.listBuilder.kanban.designer.title')}
          </h2>
          <div className="flex rounded-lg border border-border-secondary p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setView('edit')}
              className={`flex items-center gap-1 rounded px-2 py-1 ${
                view === 'edit'
                  ? 'bg-accent-600 text-white'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid="kanban-designer-view-edit"
            >
              <Pencil size={13} /> {t('tenant.listBuilder.kanban.designer.edit')}
            </button>
            <button
              type="button"
              onClick={() => setView('preview')}
              className={`flex items-center gap-1 rounded px-2 py-1 ${
                view === 'preview'
                  ? 'bg-accent-600 text-white'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid="kanban-designer-view-preview"
            >
              <Eye size={13} /> {t('tenant.listBuilder.kanban.designer.preview')}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary"
            data-testid="kanban-designer-cancel"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-lg bg-accent-600 px-4 py-1.5 text-sm text-white hover:bg-accent-700"
            data-testid="kanban-designer-save"
          >
            {t('common.save')}
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="flex min-h-0 flex-1">
          {/* Paleta */}
          {view === 'edit' && (
            <aside className="w-64 shrink-0 overflow-y-auto border-r border-border-secondary p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
                <TypeIcon size={13} /> {t('tenant.listBuilder.kanban.designer.fields')}
              </p>
              <div className="mb-4 space-y-1">
                {fields.length === 0 && (
                  <p className="text-xs text-text-muted">
                    {t('tenant.listBuilder.kanban.designer.noFields')}
                  </p>
                )}
                {fields.map((f) => (
                  <FieldPaletteItem
                    key={f.id}
                    field={f}
                    disabled={usedFieldIds.has(f.id)}
                    onAdd={() => addField(f.id)}
                  />
                ))}
              </div>

              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
                <Boxes size={13} /> {t('tenant.listBuilder.kanban.designer.elements')}
              </p>
              <div className="space-y-1">
                {layoutDefs.map((def) => (
                  <ElementPaletteItem
                    key={def.type}
                    def={def}
                    onAdd={() => addLayoutElement(def.type)}
                  />
                ))}
              </div>
            </aside>
          )}

          {/* Kanwa / podgląd */}
          <main className="min-w-0 flex-1 overflow-auto bg-bg-secondary p-6">
            <div className="mx-auto max-w-sm">
              <p className="mb-2 text-center text-xs text-text-muted">
                {t('tenant.listBuilder.kanban.designer.cardPreviewLabel')}
              </p>
              <div
                className="rounded-lg border border-border-secondary bg-bg-elevated p-3 shadow-sm"
                onClick={() => setSelectedId(null)}
                data-testid="kanban-designer-canvas"
              >
                {view === 'preview' ? (
                  <div className="space-y-1">
                    {rootChildren.length === 0 ? (
                      <p className="py-6 text-center text-xs text-text-muted">
                        {t('tenant.listBuilder.kanban.designer.emptyCard')}
                      </p>
                    ) : (
                      renderKanbanCardLayout(rootChildren, renderFieldPreview, 'preview')
                    )}
                  </div>
                ) : (
                  <CanvasList
                    containerId={ROOT_ID}
                    items={rootChildren}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    onRemove={removeElement}
                    fieldLabel={fieldLabel}
                  />
                )}
              </div>
            </div>
          </main>

          {/* Właściwości */}
          {view === 'edit' && (
            <aside className="w-72 shrink-0 overflow-y-auto border-l border-border-secondary p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                {t('tenant.listBuilder.kanban.designer.properties')}
              </p>
              {!selected ? (
                <p className="text-xs text-text-muted">
                  {t('tenant.listBuilder.kanban.designer.selectHint')}
                </p>
              ) : selected.type === 'field' ? (
                <div className="space-y-2">
                  <p className="text-xs text-text-secondary">
                    {t('tenant.listBuilder.kanban.designer.fieldProp')}
                  </p>
                  <p className="rounded border border-border-secondary bg-bg-secondary px-2 py-1 text-sm text-text-primary">
                    {fieldLabel(selected.fieldId ?? '')}
                  </p>
                  <button
                    type="button"
                    onClick={() => removeElement(selected.id)}
                    className="text-xs text-danger-600 hover:text-danger-700"
                    data-testid="kanban-designer-prop-remove"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              ) : (
                <SelectedElementProperties
                  element={selected}
                  onChangeSettings={(patch) => updateSettings(selected.id, patch)}
                  onRemove={() => removeElement(selected.id)}
                />
              )}
            </aside>
          )}
        </div>
      </DndContext>
    </div>
  );
}

// ── Paleta: pojedyncze itemy ──────────────────────────────────────────────────

function FieldPaletteItem({
  field,
  disabled,
  onAdd,
}: {
  field: CardField;
  disabled: boolean;
  onAdd: () => void;
}) {
  const dragData: PaletteDragData = { source: 'palette', kind: 'field', payload: field.id };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-field-${field.id}`,
    data: dragData,
    disabled,
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onAdd}
      disabled={disabled}
      {...attributes}
      {...listeners}
      className={`flex w-full items-center gap-2 rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-left text-xs text-text-primary transition-colors ${
        disabled
          ? 'cursor-not-allowed opacity-40'
          : isDragging
            ? 'cursor-grabbing opacity-50'
            : 'cursor-grab hover:border-accent-400'
      }`}
      data-testid={`kanban-designer-field-${field.id}`}
    >
      <TypeIcon size={12} className="shrink-0 text-text-muted" />
      <span className="flex-1 truncate font-medium">{field.label}</span>
    </button>
  );
}

function ElementPaletteItem({ def, onAdd }: { def: LayoutElementDefinition; onAdd: () => void }) {
  const dragData: PaletteDragData = {
    source: 'palette',
    kind: 'layout-element',
    payload: def.type,
  };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-element-${def.type}`,
    data: dragData,
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onAdd}
      {...attributes}
      {...listeners}
      className={`flex w-full items-center gap-2 rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-left text-xs text-text-primary transition-colors hover:border-accent-400 ${
        isDragging ? 'cursor-grabbing opacity-50' : 'cursor-grab'
      }`}
      data-testid={`kanban-designer-element-${def.type}`}
    >
      <Boxes size={12} className="shrink-0 text-text-muted" />
      <span className="flex-1 truncate font-medium">{def.label ?? def.type}</span>
    </button>
  );
}

// ── Kanwa: rekurencyjna lista + węzeł ─────────────────────────────────────────

interface CanvasListProps {
  containerId: string;
  items: LayoutElement[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  fieldLabel: (fieldId: string) => string;
}

function CanvasList({ containerId, items, ...rest }: CanvasListProps) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: `${containerId}::dropzone`,
    data: { source: 'section', target: 'section', sectionId: containerId },
  });
  return (
    <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
      <div
        ref={setNodeRef}
        className={`min-h-[2.5rem] space-y-1.5 rounded ${
          isOver ? 'ring-2 ring-accent-400 ring-inset' : ''
        } ${items.length === 0 ? 'border border-dashed border-border-secondary p-2' : ''}`}
      >
        {items.length === 0 && (
          <p className="text-center text-[11px] text-text-muted">
            {t('tenant.listBuilder.kanban.designer.dropHere')}
          </p>
        )}
        {items.map((el) => (
          <CanvasNode key={el.id} el={el} containerId={containerId} {...rest} />
        ))}
      </div>
    </SortableContext>
  );
}

interface CanvasNodeProps extends Omit<CanvasListProps, 'containerId' | 'items'> {
  el: LayoutElement;
  containerId: string;
}

function CanvasNode({
  el,
  containerId,
  selectedId,
  onSelect,
  onRemove,
  fieldLabel,
}: CanvasNodeProps) {
  const { t } = useTranslation();
  const sortable = useSortable({
    id: el.id,
    data: { source: 'element', target: 'section', sectionId: containerId },
  });
  const style = {
    transform: CSS.Translate.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : undefined,
  };
  const isSelected = selectedId === el.id;
  const container = isContainerEl(el);

  let preview: ReactNode;
  if (el.type === 'field') {
    preview = <span className="text-xs text-text-primary">{fieldLabel(el.fieldId ?? '')}</span>;
  } else if (container) {
    preview = (
      <div className="space-y-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
          {LayoutElementRegistry.get(el.type)?.label ?? el.type}
        </span>
        <CanvasList
          containerId={el.id}
          items={el.children ?? []}
          selectedId={selectedId}
          onSelect={onSelect}
          onRemove={onRemove}
          fieldLabel={fieldLabel}
        />
      </div>
    );
  } else {
    preview = LayoutElementRegistry.render({
      element: el as unknown as RegistryLayoutElement,
      children: [],
      mode: 'builder',
    });
  }

  return (
    <div
      ref={sortable.setNodeRef}
      style={style}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(el.id);
      }}
      className={`group/node relative rounded border bg-bg-primary p-2 ${
        isSelected ? 'border-accent-500 ring-1 ring-accent-400' : 'border-border-secondary'
      }`}
      data-testid={`kanban-designer-node-${el.id}`}
    >
      <span
        {...sortable.attributes}
        {...sortable.listeners}
        className="absolute left-0.5 top-0.5 cursor-grab text-text-muted opacity-0 transition group-hover/node:opacity-100"
        aria-label={t('tenant.listBuilder.kanban.designer.dragElement')}
      >
        <GripVertical size={13} />
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(el.id);
        }}
        aria-label={t('common.delete')}
        className="absolute right-0.5 top-0.5 rounded p-0.5 text-text-muted opacity-0 transition hover:text-danger-600 group-hover/node:opacity-100"
        data-testid={`kanban-designer-node-${el.id}-remove`}
      >
        <X size={13} />
      </button>
      <div className="pl-4">{preview}</div>
    </div>
  );
}

// ── Panel właściwości elementu layoutu ────────────────────────────────────────

function SelectedElementProperties({
  element,
  onChangeSettings,
  onRemove,
}: {
  element: LayoutElement;
  onChangeSettings: (patch: Record<string, unknown>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const def = LayoutElementRegistry.get(element.type);
  const Editor = def?.propertiesEditor;
  return (
    <div className="space-y-3">
      <p className="text-xs text-text-secondary">{def?.label ?? element.type}</p>
      {Editor ? (
        Editor({ settings: element.settings ?? {}, onChange: onChangeSettings })
      ) : (
        <p className="text-xs text-text-muted">{t('tenant.listBuilder.kanban.designer.noProps')}</p>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="text-xs text-danger-600 hover:text-danger-700"
        data-testid="kanban-designer-prop-remove"
      >
        {t('common.delete')}
      </button>
    </div>
  );
}
